// getProjectComparison serves two lists, told apart by `listType` on the
// request, and each keeps its own slice so they page and load independently:
// "compared" → fetchProjectComparisonApi, "notCompared" → fetchProjectNotComparedApi.
//
// Both page from the server: `from` 0 is a fresh list (first load, new search,
// new filters) and replaces what's there; anything later is a "load more" and
// appends. `searchText` is remembered on start so a slow response for an older
// search can't overwrite the rows for the current one.
const NOT_COMPARED_LIST_TYPE = 'notcompared';

const stateKeyFor = (request) => (
    request && String(request.listType || '').toLowerCase() === NOT_COMPARED_LIST_TYPE
        ? 'fetchProjectNotComparedApi'
        : 'fetchProjectComparisonApi'
);

const isFirstPage = (request) => !request || !request.from;

// Every first page sets isProcessing, but only a build-from-nothing load carries
// `isInitialFetch` — a search reload is a first page too, and the view stays on
// screen for that (so the search box keeps focus) instead of dropping back to
// the full-section loader. That difference is what the second flag is for.
const isInitialFetch = (request) => !!(request && request.isInitialFetch);

export const fetchProjectComparisonStart = (state, payload) => {
    const key = stateKeyFor(payload);
    const current = state[key];
    const firstPage = isFirstPage(payload);
    return {
        ...state,
        [key]: {
            ...current,
            isProcessing: firstPage,
            isInitialLoading: isInitialFetch(payload),
            isLoadingMore: !firstPage,
            error: false,
            searchText: payload && payload.searchText ? payload.searchText : '',
            data: firstPage ? [] : current.data,
            hasMore: firstPage ? false : current.hasMore,
            // A load that rebuilds the list makes the remembered size obsolete;
            // the response sets it again.
            baseListTotal: isInitialFetch(payload) ? null : current.baseListTotal
        }

    };
};

export const fetchProjectComparisonSuccess = (state, payload) => {
    const request = payload.payload || {};
    const key = stateKeyFor(request);
    const current = state[key];
    if ((request.searchText || '') !== current.searchText) return state;
    // A later page only fits if it starts where the list ends. A match-mode
    // change can cut the list while a page is in flight; appending that page
    // would leave a gap, so it's dropped and scrolling asks again.
    if (!isFirstPage(request) && request.from !== (current.data || []).length) return state;

    const response = payload.data && payload.data.data ? payload.data.data : {};
    const rows = response.data || [];
    const data = isFirstPage(request) ? rows : [...(current.data || []), ...rows];

    // Exact size of this list, when the response carries the totals. Both
    // lists come back with `totalAllLeaders` (everyone in scope) and
    // `totalLeaders` (the not-compared count), so the compared list's size is
    // the difference. Without them, a full page is the only sign of more.
    const hasTotals = response.totalLeaders != null;
    const listTotal = key === 'fetchProjectNotComparedApi'
        ? response.totalLeaders
        : Math.max((response.totalAllLeaders || 0) - (response.totalLeaders || 0), 0);

    // How big this list is with no search over it, kept from the load that built
    // it: a search response only reports what the search matched, so it can't
    // tell an empty list apart from one narrowed to nothing. A caller reads this
    // to skip searching a list that holds nobody in the first place, so it's
    // only trusted when the totals it's derived from actually came back — the
    // compared size is a subtraction and would read as 0 with either missing.
    const hasBaseTotal = key === 'fetchProjectNotComparedApi'
        ? hasTotals
        : (hasTotals && response.totalAllLeaders != null);

    return {
        ...state,
        [key]: {
            ...current,
            isProcessing: false,
            isInitialLoading: false,
            isLoadingMore: false,
            error: false,
            data,
            hasMore: hasTotals
                ? data.length < listTotal
                : rows.length === (response.size || request.size),
            totalLeaders: response.totalLeaders != null ? response.totalLeaders : null,
            totalAllLeaders: response.totalAllLeaders || 0,
            baseListTotal: isInitialFetch(request) && hasBaseTotal ? listTotal : current.baseListTotal
        }

    };
};

export const fetchProjectComparisonFail = (state, payload) => {
    const key = stateKeyFor(payload);
    return {
        ...state,
        [key]: {
            ...state[key],
            isProcessing: false,
            isInitialLoading: false,
            isLoadingMore: false,
            error: true,
            hasMore: false
        }

    };
};
